import { useLayoutEffect, useState } from 'react';

export type ElementBoundingRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
};

const readRect = (el: Element): ElementBoundingRect => {
  const r = el.getBoundingClientRect();
  return {
    x: r.x,
    y: r.y,
    width: r.width,
    height: r.height,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    left: r.left
  };
};

/**
 * @description
 * Tracks the bounding client rect of the first element that matches the selector, updating when
 * the element resizes or the window is resized. Safe for SSR.
 * @param selector - CSS selector for the element to measure, or null/undefined to skip
 * @returns The element's bounding client rect, or null if there is no DOM, no selector, or no match
 */
const useBoundingRectBySelector = (
  selector: string | null | undefined
): ElementBoundingRect | null => {
  const hasWindow = globalThis.window !== undefined;
  const hasDocument = globalThis.document !== undefined;
  const [rect, setRect] = useState<ElementBoundingRect | null>(null);

  useLayoutEffect(() => {
    if (!hasWindow || !hasDocument || !selector) {
      setRect(null);
      return undefined;
    }

    const el = globalThis.document.querySelector(selector);
    if (!el) {
      setRect(null);
      return undefined;
    }

    const sync = () => {
      setRect(readRect(el));
    };

    sync();

    const resizeObserver = new ResizeObserver(sync);
    resizeObserver.observe(el);

    globalThis.window.addEventListener('resize', sync);

    return () => {
      resizeObserver.disconnect();
      globalThis.window.removeEventListener('resize', sync);
    };
  }, [hasWindow, hasDocument, selector]);

  return rect;
};

export default useBoundingRectBySelector;
